import {
  BlocksChangeEvent,
  BlocksSelectEvent
} from '@blocks-web-components/events';
import {
  FORM_LABELS,
  FORM_NAMES,
  INVALID_CHARS_REGEX,
  INVALID_CHARS_STR,
  INVALID_EMAIL_REGEX,
  INVALID_STREET_ADDRESS_CHARS_REGEX,
  INVALID_STREET_CHARS_STR,
  INVALID_ZIP_CODE
} from '../constants';
import { Dispatch, SetStateAction } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { AddressBookFormBody, CountryType } from './types';

const formatString = (msg: string, ...values: (string | number)[]): string => {
  return msg.replace(/{(\d+)}/g, (match, index) => {
    const value = values[parseInt(index, 10)];
    return value !== undefined ? String(value) : match;
  });
};

const createRequiredRule = (t, fieldName: string) => ({
  required: {
    value: true,
    message: formatString(
      t('1626_FIELD_REQUIRED_ERROR'),
      t(FORM_LABELS[fieldName])
    )
  }
});

const createRuleZipcodeEmail = (t, fieldName: string, isZipCode: boolean) => ({
  pattern: {
    value: isZipCode ? INVALID_ZIP_CODE : INVALID_EMAIL_REGEX,
    message: t(isZipCode ? 'INVALID_ZIP_CODE_FORMAT' : 'INVALID_EMAIL_FORMAT')
  },
  ...createRequiredRule(t, fieldName)
});

const createRulePatternChar = (
  t,
  fieldName: string,
  regex: RegExp,
  chars: string
) => ({
  pattern: {
    value: regex,
    message: formatString(t('PLEASE_ENTER_VALUE_NOT_CHARACTERS2'), chars)
  },
  ...createRequiredRule(t, fieldName)
});

export const getFormRules = (t) => {
  const errorMessage = '1626_FIELD_REQUIRED_ERROR';
  return {
    [FORM_NAMES.FIRST_NAME]: createRulePatternChar(
      t,
      FORM_NAMES.FIRST_NAME,
      INVALID_CHARS_REGEX,
      INVALID_CHARS_STR
    ),
    [FORM_NAMES.LAST_NAME]: createRulePatternChar(
      t,
      FORM_NAMES.LAST_NAME,
      INVALID_CHARS_REGEX,
      INVALID_CHARS_STR
    ),
    [FORM_NAMES.COUNTRY]: createRequiredRule(t, FORM_NAMES.COUNTRY),
    [FORM_NAMES.STATE]: createRulePatternChar(
      t,
      FORM_NAMES.STATE,
      INVALID_CHARS_REGEX,
      INVALID_CHARS_STR
    ),
    [FORM_NAMES.CITY]: createRulePatternChar(
      t,
      FORM_NAMES.CITY,
      INVALID_CHARS_REGEX,
      INVALID_CHARS_STR
    ),
    [FORM_NAMES.STREET_ADDRESS]: createRulePatternChar(
      t,
      FORM_NAMES.STREET_ADDRESS,
      INVALID_STREET_ADDRESS_CHARS_REGEX,
      INVALID_STREET_CHARS_STR
    ),
    [FORM_NAMES.STREET_ADDRESS2]: createRulePatternChar(
      t,
      FORM_NAMES.STREET_ADDRESS2,
      INVALID_STREET_ADDRESS_CHARS_REGEX,
      INVALID_STREET_CHARS_STR
    ),
    [FORM_NAMES.ZIP_CODE]: createRuleZipcodeEmail(t, FORM_NAMES.ZIP_CODE, true),
    [FORM_NAMES.EMAIL]: createRuleZipcodeEmail(t, FORM_NAMES.EMAIL, false),
    [FORM_NAMES.PHONE]: {
      minLength: {
        value: 7,
        message: t('ERROR_INVALIDPHONE2')
      },
      required: {
        value: true,
        message: formatString(t(errorMessage), t(FORM_LABELS[FORM_NAMES.PHONE]))
      },
      validate: {
        lessThanTen: (phoneNumber: number) => {
          if (isNaN(phoneNumber)) {
            return t('ERROR_INVALIDPHONE');
          }
        }
      }
    }
  };
};

export const getFormDefaultValues = (countryCode: string) => {
  return {
    firstName: '',
    lastName: '',
    zipCode: '',
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    country: countryCode,
    countryCode: countryCode,
    phone: '',
    email: ''
  };
};

export const getNotation = (t, required: boolean) =>
  required ? t('REQUIRED_FIELD_MESSAGE') : (t('OPTIONAL') || '').toLowerCase();

export const onSelectedCountry = (
  e: BlocksSelectEvent,
  countries: CountryType[],
  setValue: UseFormSetValue<AddressBookFormBody>
) => {
  const selectedCountryCode = e.detail.value;
  const selected = countries?.find(
    (country) => country.code === selectedCountryCode
  );
  setValue('country', selected?.code);
  setValue('state', '');
};

export const handleFilteredCountries = (
  e: BlocksChangeEvent,
  setCountrySearch: Dispatch<SetStateAction<string>>
) => {
  const search = e.detail.value.trim().toLowerCase();
  setCountrySearch(search);
};
